export const CATEGORIES = ['Traffic Management', 'Construction Labour', 'Events']
export const CSV_FIELDS = [
    ['company', 'Company'], ['address', 'Address'], ['phone', 'Phone'], ['contactName', 'Additional Contact'],
    ['email', 'Email'], ['businessDescription', 'Business Description'], ['leadType', 'Lead type'],
]
// CSV supports quoted commas, escaped quotes, CRLF and multiline descriptions.
export function parseCsv(text) {
    const rows = [], row = []
    let value = '', quoted = false, closed = false
    const source = String(text).replace(/^\uFEFF/, '')
    for (let i = 0; i < source.length; i++) {
        const char = source[i]
        if (quoted) {
            if (char === '"' && source[i + 1] === '"') { value += '"'; i++ }
            else if (char === '"') { quoted = false; closed = true }
            else value += char
        } else if (char === '"') {
            if (value || closed) throw new Error('Invalid quote in CSV.')
            quoted = true
        } else if (char === ',' || char === '\n' || char === '\r') {
            row.push(value); value = ''; closed = false
            if (char !== ',') {
                if (char === '\r' && source[i + 1] === '\n') i++
                if (row.some(cell => cell.trim())) rows.push([...row])
                row.length = 0
            }
        } else if (!closed || char.trim()) {
            if (closed) throw new Error('Unexpected text after a quoted CSV cell.')
            value += char
        }
    }
    if (quoted) throw new Error('Unclosed quote in CSV.')
    row.push(value)
    if (row.some(cell => cell.trim())) rows.push(row)
    if (rows.length < 2) throw new Error('Include a header row and at least one prospect.')
    const headers = rows.shift().map(value => value.trim())
    if (new Set(headers.map(value => value.toLowerCase())).size !== headers.length) throw new Error('CSV column headings must be unique.')
    if (rows.some(row => row.length !== headers.length)) throw new Error('Some rows have a different number of columns. Check commas and quotes.')
    if (rows.length > 5000) throw new Error('Import up to 5,000 prospects at a time.')
    return { headers, rows }
}
// Spreadsheet exports routinely drop the leading zero from Australian phone
// numbers, turning 0400123456 into 400123456 and breaking both the tel: link and
// WhatsApp. Restore it on import. A number that already starts with 0 is left
// exactly as it was typed.
export function restoreLeadingZero(value) {
    const phone = String(value ?? '').trim()
    if (!phone || phone.startsWith('0')) return phone
    // Already international -- prefixing a zero would corrupt a valid number.
    if (phone.startsWith('+')) return phone
    const digits = phone.replace(/\D/g, '')
    // 61400123456 is the same stripped number with the country code kept.
    if (digits.length === 11 && digits.startsWith('61')) return '0' + digits.slice(2)
    return '0' + phone
}
export function mapProspects(csv, mapping, defaultCategory) {
    return csv.rows.map((row, index) => {
        const prospect = Object.fromEntries(CSV_FIELDS.map(([field]) => [field, mapping[field] === '' || mapping[field] == null ? '' : String(row[Number(mapping[field])] || '').trim()]))
        prospect.phone = restoreLeadingZero(prospect.phone)
        if (!prospect.company) throw new Error(`Row ${index + 2}: Company is required.`)
        const categories = (prospect.leadType || defaultCategory || '').split(/[;|,]/).map(value => value.trim()).filter(Boolean)
        prospect.categories = [...new Set(categories.map(value => {
            const match = CATEGORIES.find(category => category.toLowerCase() === value.toLowerCase())
            if (!match) throw new Error(`Row ${index + 2}: Unknown Lead type "${value}".`)
            return match
        }))]
        if (!prospect.categories.length) throw new Error(`Row ${index + 2}: Choose a Lead type or default category.`)
        if (prospect.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(prospect.email)) throw new Error(`Row ${index + 2}: Invalid email address.`)
        delete prospect.leadType
        return prospect
    })
}
export function activitySummary(activities, from, to) {
    const people = new Map()
    for (const activity of activities) {
        const date = new Date(activity.createdAt)
        if (Number.isNaN(date.getTime())) continue
        const localDate = [date.getFullYear(), String(date.getMonth() + 1).padStart(2, '0'), String(date.getDate()).padStart(2, '0')].join('-')
        if ((from && localDate < from) || (to && localDate > to)) continue
        if (!people.has(activity.actorId)) people.set(activity.actorId, { id: activity.actorId, name: activity.actorName, calls: 0, connected: 0, emails: 0, sms: 0, whatsapp: 0, notes: 0, followUps: 0 })
        const person = people.get(activity.actorId)
        if (activity.type === 'call') { person.calls++; if (activity.outcome === 'Connected') person.connected++ }
        if (activity.type === 'email' && activity.status === 'sent') person.emails++
        if (activity.type === 'sms' && activity.status === 'sent') person.sms++
        if (activity.type === 'whatsapp') person.whatsapp++
        if (activity.type === 'note') person.notes++
        if (activity.followUpDate && (!['sms', 'email'].includes(activity.type) || activity.status === 'sent')) person.followUps++
    }
    return [...people.values()].sort((a, b) => b.calls + b.emails - a.calls - a.emails)
}
